import { cn } from "@babybjorn/ui";
import type { ISbRichtext, ISbStoryData } from "gatsby-source-storyblok";
import { useEffect, useState, type FC, type ReactNode } from "react";
import { MARK_LINK, render } from "storyblok-rich-text-react-renderer";

import Link from "../Link";
import * as s from "./RichText.module.scss";

type RichTextProps = {
	content?: ISbRichtext | string;
	className?: string;
	centeredText?: boolean | null;
};

type BasicLink = {
	linktype?: string | undefined;
	href?: string | undefined;
	target?: string | undefined;
	anchor?: string | undefined;
	uuid?: string | undefined;
};

type StoryLink = BasicLink & {
	story: ISbStoryData;
};

const isStoryLink = (link: BasicLink): link is StoryLink =>
	link.linktype === "story" && "story" in link;

const addHttpsToUrl = (url: string) =>
	/^https?:\/\//i.test(url) ? url : `https://${url}`;

const RichText: FC<RichTextProps> = ({ content, className, centeredText }) => {
	const [richtextContent, setRichtextContent] = useState<ReactNode>(null);

	useEffect(
		() =>
			setRichtextContent(
				render(content, {
					markResolvers: {
						[MARK_LINK]: (children, link) => {
							const { linktype, href, target, anchor } = link;

							const translatedSlug = isStoryLink(link)
								? link.story.full_slug
								: undefined;

							const fixedHref =
								linktype === "url" && target === "_blank"
									? addHttpsToUrl(translatedSlug ?? href ?? "")
									: (translatedSlug ?? href ?? "");

							return (
								<Link
									anchor={anchor}
									href={fixedHref}
									linktype={linktype}
									target={target}
								>
									{children}
								</Link>
							);
						},
					},
					defaultBlokResolver: (name, props) => (
						<div>
							<code>
								Missing blok resolver for blok type &quot;{name}&quot;.
							</code>
							<pre>
								<code>{JSON.stringify(props, undefined, 2)}</code>
							</pre>
						</div>
					),
				}),
			),
		[content],
	);

	if (richtextContent === null) return null;

	return (
		<div
			className={cn(s.richtext, className, { [s.centeredText]: centeredText })}
		>
			{richtextContent}
		</div>
	);
};

export default RichText;
